import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";
import { BRAND } from "../../lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERESTS = ["Bridal", "Silk", "Designer", "Festive", "Just browsing"];

export const Inquiry = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    interest: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Please fill in name, email, phone & message.");
      return;
    }
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
    if (!emailOk) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      setDone(true);
      toast.success("Thank you! We'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", interest: "", message: "" });
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "We couldn't send your inquiry just now. Please try WhatsApp.";
      toast.error(typeof msg === "string" ? msg : "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative py-20 md:py-32 bg-sand/60 border-t border-line">
      <div className="container-narrow grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-5">
          <span className="overline">Inquire</span>
          <h2 className="mt-4 font-serif text-4xl md:text-6xl text-ink leading-[1.05]">
            Tell us what you're <span className="italic text-garnet">looking for</span>.
          </h2>
          <p className="mt-6 text-muteink leading-relaxed max-w-md">
            Looking for a bridal trousseau, a festive saree, or just exploring?
            Drop a note and our family will personally reach out — usually
            within a few hours.
          </p>
          <div className="gold-divider my-10" />
          <p className="text-muteink text-sm leading-relaxed max-w-md">
            Prefer chatting? Tap the WhatsApp button to reach us instantly. We
            love photos, references and Pinterest moodboards — send them along!
          </p>
          <p className="mt-4 text-sm text-muteink">
            Or email us at{" "}
            <a
              href={`mailto:${BRAND.email}`}
              data-testid="inquiry-email-link"
              className="text-garnet hover:underline break-all"
            >
              {BRAND.email}
            </a>
          </p>
        </div>

        <div className="lg:col-span-7">
          {done ? (
            <div
              data-testid="inquiry-success-state"
              className="border border-line bg-cream p-10 md:p-14 text-center"
            >
              <CheckCircle2 className="mx-auto text-garnet" size={42} strokeWidth={1.2} />
              <h3 className="mt-6 font-serif text-3xl text-ink">
                Thank you, beautifully done.
              </h3>
              <p className="mt-3 text-muteink max-w-md mx-auto">
                We've received your inquiry and will reach out within a few
                hours. In the meantime, feel free to browse our latest edit.
              </p>
              <button
                onClick={() => setDone(false)}
                className="btn-outline-dark mt-8"
                data-testid="inquiry-send-another-btn"
              >
                Send another inquiry
              </button>
            </div>
          ) : (
            <form
              onSubmit={onSubmit}
              data-testid="inquiry-form"
              className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-7 bg-cream border border-line p-8 md:p-12"
            >
              <div className="sm:col-span-1">
                <label className="overline">Name</label>
                <input
                  type="text"
                  data-testid="inquiry-input-name"
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Your full name"
                  className="underline-input mt-2"
                  required
                />
              </div>
              <div className="sm:col-span-1">
                <label className="overline">Phone</label>
                <input
                  type="tel"
                  data-testid="inquiry-input-phone"
                  value={form.phone}
                  onChange={update("phone")}
                  placeholder="+91"
                  className="underline-input mt-2"
                  required
                />
              </div>
              <div className="sm:col-span-2">
                <label className="overline">Email</label>
                <input
                  type="email"
                  data-testid="inquiry-input-email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="you@example.com"
                  className="underline-input mt-2"
                  required
                />
              </div>

              <div className="sm:col-span-2">
                <label className="overline">I'm interested in</label>
                <div className="flex flex-wrap gap-2 mt-3">
                  {INTERESTS.map((i) => (
                    <button
                      key={i}
                      type="button"
                      data-testid={`inquiry-interest-${i.toLowerCase().replace(/\s/g, "-")}`}
                      onClick={() => setForm((s) => ({ ...s, interest: i }))}
                      className={`px-4 py-2 text-xs tracking-[0.18em] uppercase border transition-all ${
                        form.interest === i
                          ? "bg-garnet text-cream border-garnet"
                          : "border-line text-ink hover:border-garnet"
                      }`}
                    >
                      {i}
                    </button>
                  ))}
                </div>
              </div>

              <div className="sm:col-span-2">
                <label className="overline">Message</label>
                <textarea
                  data-testid="inquiry-input-message"
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Tell us about the occasion, colours you love, budget if any…"
                  rows={4}
                  className="underline-input mt-2 resize-none"
                  required
                />
              </div>

              <div className="sm:col-span-2 flex items-center justify-between gap-4 pt-2">
                <p className="text-xs text-muteink">
                  We'll never share your details. Promise.
                </p>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="inquiry-form-submit-button"
                  className="btn-primary disabled:opacity-60"
                >
                  {loading ? (
                    <>
                      <Loader2 size={16} className="animate-spin" /> Sending
                    </>
                  ) : (
                    <>Send Inquiry</>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

export default Inquiry;
